/**
 * WizardTopbar — wspólny górny pasek kreatora (montowany w StudioTopbar przez `topbarSlot`).
 *
 * Jedno źródło prawdy dla "szkieletu" (chrome) WSZYSTKICH kreatorów:
 * Hodowla, Firma, Suka/Reproduktor (Dog) i Miot (Litter) używają DOKŁADNIE tego samego paska.
 *
 * Zawiera: licznik kroku + pasek postępu, "Zapisz i wróć", "Zobacz stronę" (gdy jest slug)
 * oraz przycisk główny "Dalej / Opublikuj".
 *
 * Per-kreator różni się WYŁĄCZNIE treścią (etykieta publikacji, link strony) i `prefix`
 * dla data-testid — wygląd i układ są identyczne.
 */
import { Loader2, Check, Save, ArrowRight, Globe } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export const WizardTopbar = ({
  prefix,
  step,
  totalSteps,
  stepName,
  progressPct,
  isLastStep,
  submitting,
  nextDisabled,
  publishLabel = 'Opublikuj',
  onSaveExit,
  onNext,
  viewHref,
}) => {
  return (
    <div className="flex items-center gap-3 w-full" data-testid={`${prefix}-topbar-slot`}>
      <div className="flex-1 min-w-0 hidden sm:block">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-bold text-[var(--apisai-primary)] whitespace-nowrap">
            Krok {step} z {totalSteps}
          </span>
          <span className="text-xs text-[var(--apisai-text-muted)] truncate">· {stepName || ''}</span>
        </div>
        <Progress
          value={progressPct}
          className="h-1.5 mt-1.5 bg-[var(--apisai-primary-soft)] [&>div]:bg-gradient-to-r [&>div]:from-[var(--apisai-primary)] [&>div]:to-[var(--apisai-primary-700)]"
        />
      </div>

      {onSaveExit && (
        <button
          type="button"
          onClick={onSaveExit}
          disabled={submitting}
          data-testid={`${prefix}-save-exit-btn`}
          className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--apisai-primary-ring)] text-[var(--apisai-primary-700)] hover:bg-[var(--apisai-primary-soft)] font-medium text-sm transition-colors disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" />
          <span className="hidden md:inline">Zapisz i wróć</span>
        </button>
      )}

      {viewHref && (
        <a
          href={viewHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${prefix}-view-page-btn`}
          className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-400/40 text-emerald-700 bg-emerald-50/60 hover:bg-emerald-100/80 font-medium text-sm transition-colors"
          title="Otwórz opublikowaną stronę w nowej karcie"
        >
          <Globe className="w-3.5 h-3.5" />
          <span className="hidden md:inline">Zobacz stronę</span>
        </a>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        data-testid={`${prefix}-next-btn`}
        className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white font-semibold text-sm shadow-[var(--shadow-primary)] disabled:opacity-50 transition-colors"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isLastStep ? (
          <>
            <Check className="w-4 h-4" />
            <span className="hidden sm:inline">{publishLabel}</span>
          </>
        ) : (
          <>
            <span className="hidden sm:inline">Dalej</span>
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    </div>
  );
};

export default WizardTopbar;
